import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { getTierByProductId, STRIPE_TIERS, TierKey } from "@/config/stripe";

interface SubscriptionState {
  isLoading: boolean;
  subscribed: boolean;
  currentTier: TierKey;
  subscriptionEnd: string | null;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionState>({
  isLoading: true,
  subscribed: false,
  currentTier: "free",
  subscriptionEnd: null,
  refresh: async () => {},
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const { isAdmin } = useRole();
  const [isLoading, setIsLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [currentTier, setCurrentTier] = useState<TierKey>("free");
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);

  const effectiveTier: TierKey = isAdmin ? "business" : currentTier;
  const effectiveSubscribed = isAdmin ? true : subscribed;

  const checkSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setSubscribed(false);
      setCurrentTier("free");
      setSubscriptionEnd(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      setSubscribed(data.subscribed ?? false);
      setCurrentTier(getTierByProductId(data.product_id));
      setSubscriptionEnd(data.subscription_end ?? null);
    } catch (err) {
      console.error("Failed to check subscription:", err);
    } finally {
      setIsLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  return (
    <SubscriptionContext.Provider value={{ isLoading, subscribed: effectiveSubscribed, currentTier: effectiveTier, subscriptionEnd, refresh: checkSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
